/**
 * Accessibility utilities for I Got Soul platform
 * Supports WCAG AA compliance, screen readers (NVDA, VoiceOver, TalkBack, JAWS)
 */

/**
 * Announce a message to screen readers via live region
 * @param {string} message - The message to announce
 * @param {string} priority - 'polite' or 'assertive'
 */
export const announceToScreenReader = (message, priority = 'polite') => {
  // Use a persistent live region if available, or create one
  let liveRegion = document.getElementById('sr-live-region');
  
  if (!liveRegion) {
    liveRegion = document.createElement('div');
    liveRegion.id = 'sr-live-region';
    liveRegion.setAttribute('aria-live', priority);
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.setAttribute('class', 'sr-only');
    liveRegion.style.cssText = 'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;';
    document.body.appendChild(liveRegion);
  }
  
  // Defer the announcement to avoid interfering with focus management
  // Using queueMicrotask ensures this runs after the current task but before rendering
  queueMicrotask(() => {
    // Clear first, then set new text to ensure screen reader picks up the change
    liveRegion.textContent = '';
    requestAnimationFrame(() => {
      liveRegion.textContent = message;
    });
  });
};

/**
 * Handle keyboard navigation for menu items
 * @param {KeyboardEvent} event - The keyboard event
 * @param {HTMLElement[]} items - Array of focusable items
 * @param {number} currentIndex - Current focused item index
 * @param {Function} onClose - Function to close the menu
 * @returns {number} - New focus index
 */
export const handleMenuKeyboardNav = (event, items, currentIndex, onClose) => {
  let newIndex = currentIndex;
  
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      newIndex = (currentIndex + 1) % items.length;
      break;
    case 'ArrowUp':
      event.preventDefault();
      newIndex = currentIndex === 0 ? items.length - 1 : currentIndex - 1;
      break;
    case 'Home':
      event.preventDefault();
      newIndex = 0;
      break;
    case 'End':
      event.preventDefault();
      newIndex = items.length - 1;
      break;
    case 'Escape':
      event.preventDefault();
      onClose?.();
      return -1;
    case 'Tab':
      // Allow natural tab behavior but close menu
      onClose?.();
      return -1;
    default:
      // Character search - jump to item starting with pressed key
      const char = event.key.toLowerCase();
      if (char.length === 1 && char.match(/[a-z]/i)) {
        const matchIndex = items.findIndex(
          (item, idx) => idx > currentIndex && 
            item.textContent?.toLowerCase().startsWith(char)
        );
        if (matchIndex !== -1) {
          newIndex = matchIndex;
        } else {
          // Wrap around and search from beginning
          const wrapIndex = items.findIndex(
            item => item.textContent?.toLowerCase().startsWith(char)
          );
          if (wrapIndex !== -1) newIndex = wrapIndex;
        }
      }
  }
  
  items[newIndex]?.focus();
  return newIndex;
};

/**
 * Generate a unique ID for ARIA relationships
 * @param {string} prefix - ID prefix
 * @returns {string} - Unique ID
 */
export const generateAriaId = (prefix = 'aria') => {
  return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * CSS class for visually hidden but screen reader accessible content
 */
export const SR_ONLY_CLASS = 'sr-only';

/**
 * Focus trap utility for modals and dropdowns
 * @param {HTMLElement} container - The container to trap focus within
 * @returns {Function} - Cleanup function
 */
export const createFocusTrap = (container) => {
  const focusableElements = container.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];
  
  const handleKeyDown = (e) => {
    if (e.key !== 'Tab') return;
    
    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement?.focus();
      }
    } else {
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement?.focus();
      }
    }
  };
  
  container.addEventListener('keydown', handleKeyDown);
  firstElement?.focus();
  
  return () => {
    container.removeEventListener('keydown', handleKeyDown);
  };
};

/**
 * Check if reduced motion is preferred
 * @returns {boolean}
 */
export const prefersReducedMotion = () => {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * Get contrasting text color for a background
 * Ensures WCAG AA compliance (4.5:1 for normal text, 3:1 for large text)
 * @param {string} backgroundColor - Hex color
 * @returns {string} - 'white' or 'black'
 */
export const getContrastingTextColor = (backgroundColor) => {
  // Remove # if present
  const hex = backgroundColor.replace('#', '');
  
  // Convert to RGB
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  return luminance > 0.5 ? 'black' : 'white';
};
